import { useState, useEffect } from "react";
import {
  DollarSign,
  TrendingDown,
  Calendar,
  RefreshCw,
  Target,
} from "lucide-react";
import { useMasterData } from "../hooks/useMasterData";
import { supabase } from "../integrations/supabase/client";
import { MetricCard } from "../components/dashboard/MetricCard";
import { PerformanceChart } from "../components/dashboard/PerformanceChart";

export function Revenue() {
  const { revenueMetrics, dashboardMetrics } = useMasterData();
 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [revenueData, setRevenueData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [mrrGrowth, setMrrGrowth] = useState(0);

  useEffect(() => {
    if (dashboardMetrics?.revenueData?.length) {
      setRevenueData(dashboardMetrics.revenueData);
      setMrrGrowth(dashboardMetrics.mrrGrowth ?? 0);
    } else {
      fetchRevenueData();
    }
  }, [dashboardMetrics]);

  const fetchRevenueData = async () => {
    setLoading(true);
    try {
      const { data: revenueDataReal, error } = await supabase.rpc(
        "get_master_revenue_data",
      );

      if (error) throw error;

      if (revenueDataReal && revenueDataReal.length > 0) {
        // Transformar dados da RPC para formato do gráfico
 
        setRevenueData(
// eslint-disable-next-line @typescript-eslint/no-explicit-any
          revenueDataReal.map((r: any) => ({
            month: r.month || r.period,
            mrr: r.revenue || r.mrr || 0,
            new: r.new_mrr || 0,
            churn: r.churn_mrr || 0,
          })),
        );
      } else {
        setRevenueData([]);
      }

      // Calcular crescimento (comparar último mês com penúltimo)
      if (revenueDataReal && revenueDataReal.length >= 2) {
        const last = revenueDataReal[revenueDataReal.length - 1];
        const previous = revenueDataReal[revenueDataReal.length - 2];
        const growth =
          previous.mrr > 0
            ? ((last.mrr - previous.mrr) / previous.mrr) * 100
            : 0;
        setMrrGrowth(growth);
      }
    } catch (error) {
      console.error("Error fetching revenue data:", error);
      setRevenueData([]);
      setMrrGrowth(0);
    } finally {
      setLoading(false);
    }
  };

  const currentMRR =
    revenueMetrics?.mrr ||
    (revenueData.length > 0 ? revenueData[revenueData.length - 1].mrr : 0);
  const arr = revenueMetrics?.arr || currentMRR * 12;
  const avgTicket =
    revenueMetrics?.arpu || (currentMRR > 0 ? currentMRR / 72 : 0);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Receita</h1>
          <p className="text-slate-400">
            Análise financeira e métricas de receita
          </p>
        </div>
        <button
          onClick={fetchRevenueData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-slate-300 hover:bg-white/10 transition-all"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Atualizar
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="MRR Atual"
          value={`R$ ${currentMRR.toLocaleString()}`}
          change={mrrGrowth}
          icon={<DollarSign className="w-6 h-6" />}
          gradient
          color="emerald"
          subtitle="Receita Recorrente Mensal"
        />
        <MetricCard
          title="ARR Projetado"
          value={`R$ ${arr.toLocaleString()}`}
          icon={<Calendar className="w-6 h-6" />}
          color="blue"
          subtitle="Receita Recorrente Anual"
        />
        <MetricCard
          title="Ticket Médio"
          value={`R$ ${avgTicket.toFixed(0)}`}
          icon={<Target className="w-6 h-6" />}
          color="amber"
          subtitle="ARPU (Average Revenue Per User)"
        />
        <MetricCard
          title="Churn MRR"
          value={`R$ ${revenueData.length > 0 ? revenueData[revenueData.length - 1].churn || 0 : 0}`}
          icon={<TrendingDown className="w-6 h-6" />}
          color="red"
          subtitle="Perda de receita por churn"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PerformanceChart
          data={revenueData.map((r) => ({ month: r.month, revenue: r.mrr }))}
          type="area"
          dataKeys={["revenue"]}
          colors={["#22c55e"]}
          title="Evolução MRR"
          subtitle="Últimos 6 meses"
          formatValue={(v) => `R$ ${(v / 1000).toFixed(0)}k`}
        />
        <PerformanceChart
          data={revenueData}
          type="bar"
          dataKeys={["new", "churn"]}
          colors={["#22c55e", "#ef4444"]}
          title="Novos vs Churn"
          subtitle="Comparativo mensal"
          formatValue={(v) => `R$ ${(v / 1000).toFixed(0)}k`}
        />
      </div>
    </div>
  );
}
